// @dynamic-i18n-keys
//   packagePicker.heroTable.header.noCoreWithLogo
//   packagePicker.heroTable.header.noCoreWithLogo.mobile
//   packagePicker.heroTable.header.upgradeWithLogo
//   packagePicker.heroTable.header.upgradeWithLogo.mobile
//   packagePicker.heroTable.header.upgrade.infoV2
//   packagePicker.heroTable.header.upgrade.infoV2.mobile
import type { FC } from 'react';
import { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import DACoreLogo from '@wix/da-ds/pkg/DACoreLogo';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { PapiSubscriptionV3 } from '@wix/da-papi-types';
import { type PackagePickerStoreSlice } from '../../../../../redux/packagePicker/types';
import PlanExpiringNote from './PlanExpiringNote';
import { getHasProratedDiscountV3 } from '../../../../../redux/packagePicker/selectors';
import GiftRecipient from './GiftRecipient';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import s from './HeroHeader.scss';

export interface Props {
  hasCore?: boolean;
  showSubtitle?: boolean;
  giftUser?: PackagePickerStoreSlice['giftUser'];
  sale?: PackagePickerStoreSlice['sale'];
  currentCoreSubscription?: PapiSubscriptionV3;
  className?: string;
}

const HeroHeader: FC<Props> = ({
  hasCore,
  giftUser,
  currentCoreSubscription,
  showSubtitle,
  className,
}) => {
  const { t } = useTranslation();
  const isDesktop = !useContext(MobileContext);
  const hasProratedDiscount = useSelector(getHasProratedDiscountV3);
  const planWillExpire = currentCoreSubscription?.willExpire;
  const planEndTs = currentCoreSubscription?.endts;
  const hasSubscription = Boolean(currentCoreSubscription);

  if (giftUser) {
    return (
      <div className={cn(s['root'], className)}>
        <Heading className={s['header']}>
          {t('packagePicker.heroTable.header.gift')}
        </Heading>
        <GiftRecipient user={giftUser} />
        <div className={s['manage-info']}>
          {t('packagePicker.heroTable.header.gift.below')}
        </div>
      </div>
    );
  }

  if (!hasCore && !hasSubscription) {
    return (
      <div className={cn(s['root'], className)}>
        <Heading className={s['header']}>
          <Trans
            i18nKey={`packagePicker.heroTable.header.noCoreWithLogo${
              isDesktop ? '' : '.mobile'
            }`}
          >
            Upgrade to a <DACoreLogo className={s['core-logo']} /> Plan
          </Trans>
        </Heading>
        {showSubtitle && (
          <Heading className={s['subheader']}>
            {t('packagePicker.heroTable.header.createPromoteMonetize')}
          </Heading>
        )}
      </div>
    );
  }

  return (
    <div className={cn(s['root'], className)}>
      {planWillExpire && planEndTs && (
        <PlanExpiringNote endTimestamp={planEndTs} />
      )}
      <Heading className={s['header']}>
        <Trans
          i18nKey={`packagePicker.heroTable.header.upgradeWithLogo${
            isDesktop ? '' : '.mobile'
          }`}
        >
          Upgrade Your <DACoreLogo className={s['core-logo']} /> Plan
        </Trans>
      </Heading>
      {showSubtitle && (
        <Heading className={s['subheader']}>
          {t(
            !hasProratedDiscount
              ? 'packagePicker.heroTable.header.createPromoteMonetize'
              : `packagePicker.heroTable.header.upgrade.infoV2${
                  isDesktop ? '' : '.mobile'
                }`
          )}
        </Heading>
      )}
    </div>
  );
};
HeroHeader.displayName = 'HeroHeader';

export default HeroHeader;
